import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { PageHeader, LoadingSpinner } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useFormatter } from '../utils/formatters';
import api from '../services/api';
import { toast } from 'sonner';
import { FileDown, Printer, Mail, Download, Calendar, TrendingDown, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const REPORT_TYPES = [
  { value: 'spend-by-supplier', label: 'Top Spend by Supplier', endpoint: '/api/reports/spend-by-supplier' },
  { value: 'spend-by-account-code', label: 'Top Spend by Account Code', endpoint: '/api/reports/spend-by-account-code' },
  { value: 'spend-by-category', label: 'Top Spend by Category Code', endpoint: '/api/reports/spend-by-category' },
  { value: 'spend-by-buyer', label: 'Top Spend by Buyer', endpoint: '/api/reports/spend-by-buyer' },
  { value: 'spend-by-delivery-point', label: 'Top Spend by Delivery Point', endpoint: '/api/reports/spend-by-delivery-point' },
  { value: 'spend-by-invoice-point', label: 'Top Spend by Invoice Point', endpoint: '/api/reports/spend-by-invoice-point' },
  { value: 'spend-by-document-type', label: 'Top Spend by Document Type', endpoint: '/api/reports/spend-by-document-type' },
];

export default function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { fmt } = useFormatter();
  const navigate = useNavigate();
  
  const [reportType, setReportType] = useState(searchParams.get('type') || 'spend-by-supplier');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const reportConfig = REPORT_TYPES.find(r => r.value === reportType);
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const res = await api.get(reportConfig.endpoint, { params });
      setReportData(res.data);
    } catch (e) {
      toast.error('Failed to generate report');
      console.error(e);
    }
    setLoading(false);
  }, [reportType, dateFrom, dateTo]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam && REPORT_TYPES.find(r => r.value === typeParam)) {
      setReportType(typeParam);
    }
  }, [searchParams]);

  const exportToCSV = () => {
    if (!reportData || !reportData.data.length) return;
    
    // Build CSV
    const headers = Object.keys(reportData.data[0]);
    const csvRows = [headers.join(',')];
    
    reportData.data.forEach(row => {
      const values = headers.map(h => {
        const val = row[h];
        return typeof val === 'string' && val.includes(',') ? `"${val}"` : val;
      });
      csvRows.push(values.join(','));
    });
    
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportType}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported to CSV');
  };

  const printReport = () => {
    window.print();
    toast.success('Opening print dialog...');
  };

  const emailReport = () => {
    const subject = encodeURIComponent(reportData?.report_title || 'Spend Analysis Report');
    const body = encodeURIComponent(`Please find the ${reportData?.report_title} report.\n\nGenerated on: ${new Date().toLocaleString()}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    toast.info('Opening email client...');
  };

  const renderTable = () => {
    if (!reportData || !reportData.data.length) {
      return <p className="text-sm text-muted-foreground text-center py-8">No data available for this report.</p>;
    }

    const keys = Object.keys(reportData.data[0]);
    
    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              {keys.map(key => (
                <TableHead key={key} className={key.includes('spend') || key.includes('total') ? 'text-right' : ''}>
                  {key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {reportData.data.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                {keys.map(key => (
                  <TableCell key={key} className={key.includes('spend') || key.includes('total') ? 'text-right font-mono' : ''}>
                    {key.includes('spend') || key.includes('total') ? fmt(row[key]) : row[key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <AppShell title="Reports">
      <PageHeader 
        title="Spend Analysis Reports" 
        subtitle="Generate, export, and share detailed spend reports"
      />

      {/* Savings Report Link */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="border rounded-xl p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/reports/savings')} data-testid="savings-report-link">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 rounded-full p-2"><TrendingDown className="w-5 h-5 text-emerald-700" /></div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm text-emerald-900">Procurement Savings Report</h3>
              <p className="text-xs text-emerald-700">Savings by event, supplier, and category. Excel & PDF export.</p>
            </div>
          </div>
        </div>
        <div className="border rounded-xl p-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/reports/traceability')} data-testid="traceability-report-link">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-100 rounded-full p-2"><Shield className="w-5 h-5 text-indigo-700" /></div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm text-indigo-900">Award Decision Traceability</h3>
              <p className="text-xs text-indigo-700">Full audit trail of award decisions with downstream procurement links.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6 print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="reportType" className="text-xs font-semibold mb-1.5 block">Report Type</Label>
            <Select value={reportType} onValueChange={(val) => {
              setReportType(val);
              setSearchParams({ type: val });
            }}>
              <SelectTrigger id="reportType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REPORT_TYPES.map(rt => (
                  <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="dateFrom" className="text-xs font-semibold mb-1.5 block">Date From</Label>
            <Input 
              id="dateFrom" 
              type="date" 
              value={dateFrom} 
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          
          <div>
            <Label htmlFor="dateTo" className="text-xs font-semibold mb-1.5 block">Date To</Label>
            <Input 
              id="dateTo" 
              type="date" 
              value={dateTo} 
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          
          <div className="flex items-end">
            <Button onClick={loadReport} className="w-full">
              <Calendar className="h-4 w-4 mr-2" />
              Generate Report
            </Button>
          </div>
        </div>
      </Card>

      {/* Actions */}
      {reportData && reportData.data.length > 0 && (
        <div className="flex gap-2 mb-4 print:hidden">
          <Button variant="outline" size="sm" onClick={exportToCSV}>
            <FileDown className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={printReport}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" size="sm" onClick={emailReport}>
            <Mail className="h-4 w-4 mr-2" />
            Email
          </Button>
        </div>
      )}

      {/* Report Content */}
      <Card className="p-6">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            {/* Report Header */}
            {reportData && (
              <div className="mb-6 pb-4 border-b">
                <h2 className="text-xl font-bold mb-1">{reportData.report_title}</h2>
                <div className="flex gap-4 text-xs text-muted-foreground">
                  <span>Generated: {new Date(reportData.generated_at).toLocaleString('en-GB')}</span>
                  {reportData.date_from && <span>From: {reportData.date_from}</span>}
                  {reportData.date_to && <span>To: {reportData.date_to}</span>}
                  <span>Records: {reportData.data.length}</span>
                </div>
              </div>
            )}
            
            {/* Report Table */}
            {renderTable()}
          </>
        )}
      </Card>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:hidden {
            display: none !important;
          }
          main > div:last-child,
          main > div:last-child * {
            visibility: visible;
          }
          main > div:last-child {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </AppShell>
  );
}
